import Text from "common/Typography/Text";
import React, { ChangeEvent, useContext, useEffect, useRef } from "react";
import { FixMeLater } from "types";
import {
  ClientData,
  ConfigurationExchangeInfo,
  LanguageNameSpaces,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import useSelectSubExchange from "@shared/features/portfolio/createAccount/useSelectSubExchange";
import Input from "common/Inputs/Input";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "@shared/common/constants";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useBottomNavigator } from "utils/hooks/misc";
import Checkbox from "common/Checkbox/Checkbox";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import {
  CREATE_ACCOUNT_ACCOUNT_NAME_ID,
  CREATE_ACCOUNT_SUB_EXCHANGE_CHECKBOX_ID,
} from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookSelectSubExchange from "features/portfolio/CreateAccount/NewLook/NewLookSelectSubExchange";
import { phoneMinWidth } from "common/NewLook/layout.ts";

interface SelectSubExchangeProps {
  primaryExchange?: string;
  isEdit?: boolean;
  account?: ClientData;
  isLoading?: boolean;
}

const SelectSubExchange = (props: SelectSubExchangeProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookSelectSubExchange {...props} />;
  return <ClassicSelectSubExchange {...props} />;
};

const ClassicSelectSubExchange = ({
  primaryExchange,
  account,
  isEdit,
  isLoading,
}: SelectSubExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(undefined);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const bottomNavigatorData = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const {
    subExchanges,
    getCheckboxData,
    accountNameInputData,
    accountNameInitialized,
  } = useSelectSubExchange({
    primaryExchange,
    account,
    isEdit,
  });

  useEffect(() => {
    if (!bottomNavigatorData.baseShouldDisplay) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [inputRef]);

  const accountNameInput = (
    <div style={{ marginTop: isEdit ? 0 : 24, marginBottom: 24 }}>
      <InputLabel>{accountNameInputData.label}</InputLabel>
      <Input
        ref={inputRef as FixMeLater}
        value={accountNameInputData.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          accountNameInputData.onChange(e.target.value)
        }
        data-testid={CREATE_ACCOUNT_ACCOUNT_NAME_ID}
      />
      <RequiredFieldMessage
        requirement={!!accountNameInputData.error}
        errorMessage={accountNameInputData.error || ""}
        style={{ marginTop: 4 }}
      />
    </div>
  );

  const getCheckbox = (subExchange: ConfigurationExchangeInfo) => {
    const checkboxData = getCheckboxData(subExchange);
    return (
      <StyledCheckbox
        key={checkboxData.key}
        value={checkboxData.value}
        checked={checkboxData.checked}
        onChange={checkboxData.onChange}
        $checked={checkboxData.checked}
        data-testid={`${CREATE_ACCOUNT_SUB_EXCHANGE_CHECKBOX_ID}-${subExchange.exchange}`}
      >
        <Title
          level={4}
          type={checkboxData.exchangeError ? "danger" : "primary"}
        >
          {checkboxData.exchange}
          {checkboxData.account && (
            <AccountName $error={!!checkboxData.accountError}>
              {checkboxData.account}
            </AccountName>
          )}
        </Title>
        <Description $isMobile={isMobile}>
          {checkboxData.description}
        </Description>
      </StyledCheckbox>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={
        !isLoading && !accountNameInitialized && !isExternalConnectAccount
      }
      backgroundColor={theme.colors.layoutBgColor}
    >
      {!isEdit && (
        <div>
          <StyledText>{t("create_account.sub_exchanges_question")}</StyledText>
          <SubExchangesWrapper>
            {subExchanges?.map((subExchange) => getCheckbox(subExchange))}
          </SubExchangesWrapper>
        </div>
      )}
      {accountNameInputData.shouldDisplay && accountNameInput}
    </LoadingSpinnerOverlay>
  );
};

export default SelectSubExchange;

const StyledText = styled(Text)`
  margin-bottom: 12px;
`;
const SubExchangesWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
`;
const StyledCheckbox = styled(Checkbox)<{ $checked?: boolean }>`
  .ant-checkbox {
    align-self: flex-start;
    top: 3px;
  }
  margin-right: 4px;
  padding: 12px;
  border-radius: 4px;
  width: 100%;
  ${(p) =>
    p.$checked &&
    `
    background: ${
      p.theme.isDarkMode
        ? p.theme.colors.cardLightBgColor
        : p.theme.colors.statisticsBgColor
    };
  `};
  @media (min-width: ${phoneMinWidth}) {
    &:hover {
      background: ${(p) =>
        p.theme.isDarkMode
          ? p.theme.colors.cardLightBgColor
          : p.theme.colors.statisticsBgColor};
    }
  }
`;
const Description = styled(Text)<{ $isMobile?: boolean }>`
  color: ${(p) => p.theme.colors.mediumGrey};
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
const AccountName = styled.span<{ $error: boolean }>`
  margin-left: 4px;
  color: ${(p) =>
    p.$error ? p.theme.colors.red : p.theme.colors.secondaryGrey};
`;
const InputLabel = styled(Text)`
  padding-bottom: 8px;
  color: ${(p) => p.theme.colors.basic};
`;
